/**
 * useGameSocket Hook
 *
 * Custom React hook for managing Socket.IO connection and game events.
 * Handles all socket communication for the game.
 *
 * Used by:
 * - GamePage (main game container)
 * - Phase components (when they need socket events)
 *
 * Responsibilities:
 * - Provide socket connection and event handling functions
 * - Subscribe to game events (room updates, game state changes, etc.)
 * - Emit game actions (join room, place monster, move, etc.)
 * - Clean up on unmount
 * 
 * Note: Connection must be initiated manually via connectAndJoinRoom().
 * The hook does not automatically connect on mount.
 *
 * Returns:
 * - socket instance
 * - Connection status
 * - Event handlers (wrapped functions)
 * - Emit functions (wrapped actions)
 *
 * Replaces: Socket logic scattered throughout RoomPage.js
 */

import { useEffect, useState, useCallback } from 'react';
import gameSocketService, { type ConnectionStatus } from '../services/gameSocket.service';
import type {
    // Client emit types
    JoinRoomPayload,
    LeaveRoomPayload,
    PlayerReadyPayload,
    PlaceIslandsPayload,
    PlaceMonsterPayload,
    MoveMonsterPayload,
    UpdateMonsterDeductionPayload,
    UpdateMonsterDeductionResponse,
    GetPlayerDeductionsResponse,
    ChatMessagePayload,
    GetChatHistoryPayload,
    GetChatHistoryResponse,
    GetHistoryPayload,
    ChangeThemePayload,
    ChangeColorPayload,
    // Server listen types
    RoomUpdatePayload,
    PlayerJoinedPayload,
    PlayerLeftPayload,
    PlayerReadyUpdatePayload,
    PhaseChangedPayload,
    IslandsPlacedPayload,
    MonsterPlacedPayload,
    MonsterMovedPayload,
    GameEndedPayload,
    TurnChangedPayload,
    ChatMessageReceivedPayload,
    HistoryUpdatePayload,
    DisconnectTimerPayload,
    PlayerReconnectedPayload,
    PlayerDisconnectedPayload,
    SocketErrorPayload,
    // Response types
    JoinRoomResponse,
    PlaceIslandsResponse,
    PlaceMonsterResponse,
    MoveMonsterResponse,
    ConcedeResponse,
    GetHistoryResponse,
    SocketResponse,
    StartGameResponse,
} from '../types/socket.types';

export interface UseGameSocketReturn {
    // Connection info
    socket: ReturnType<typeof gameSocketService.getSocket>;
    connectionStatus: ConnectionStatus;
    isConnected: boolean;

    // Connection and room management
    connectAndJoinRoom: (
        payload: JoinRoomPayload,
        options?: { timeout?: number; maxRetries?: number }
    ) => Promise<JoinRoomResponse>;
    joinRoom: (payload: JoinRoomPayload, callback?: (response: JoinRoomResponse) => void) => void;
    leaveRoom: (payload: LeaveRoomPayload, callback?: (response: SocketResponse) => void) => void;
    disconnect: () => void;
    playerReady: (payload: PlayerReadyPayload, callback?: (response: SocketResponse) => void) => void;
    placeIslands: (payload: PlaceIslandsPayload, callback?: (response: PlaceIslandsResponse) => void) => void;
    placeMonster: (payload: PlaceMonsterPayload, callback?: (response: PlaceMonsterResponse) => void) => void;
    moveMonster: (payload: MoveMonsterPayload, callback?: (response: MoveMonsterResponse) => void) => void;
    concede: (callback?: (response: ConcedeResponse) => void) => void;
    updateMonsterDeduction: (
        payload: UpdateMonsterDeductionPayload,
        callback?: (response: UpdateMonsterDeductionResponse) => void
    ) => void;
    getPlayerDeductions: (callback?: (response: GetPlayerDeductionsResponse) => void) => void;
    sendChatMessage: (payload: ChatMessagePayload, callback?: (response: SocketResponse) => void) => void;
    getChatHistory: (payload: GetChatHistoryPayload, callback?: (response: GetChatHistoryResponse) => void) => void;
    getHistory: (payload: GetHistoryPayload, callback?: (response: GetHistoryResponse) => void) => void;
    changeTheme: (payload: ChangeThemePayload, callback?: (response: SocketResponse) => void) => void;
    changeColor: (payload: ChangeColorPayload, callback?: (response: SocketResponse) => void) => void;
    startGame: (callback?: (response: StartGameResponse) => void) => void;

    // Subscribe functions (returns unsubscribe function)
    onRoomUpdate: (handler: (payload: RoomUpdatePayload) => void) => () => void;
    onPlayerJoined: (handler: (payload: PlayerJoinedPayload) => void) => () => void;
    onPlayerLeft: (handler: (payload: PlayerLeftPayload) => void) => () => void;
    onPlayerReadyUpdate: (handler: (payload: PlayerReadyUpdatePayload) => void) => () => void;
    onPhaseChanged: (handler: (payload: PhaseChangedPayload) => void) => () => void;
    onIslandsPlaced: (handler: (payload: IslandsPlacedPayload) => void) => () => void;
    onMonsterPlaced: (handler: (payload: MonsterPlacedPayload) => void) => () => void;
    onMonsterMoved: (handler: (payload: MonsterMovedPayload) => void) => () => void;
    onGameEnded: (handler: (payload: GameEndedPayload) => void) => () => void;
    onTurnChanged: (handler: (payload: TurnChangedPayload) => void) => () => void;
    onChatMessage: (handler: (payload: ChatMessageReceivedPayload) => void) => () => void;
    onHistoryUpdate: (handler: (payload: HistoryUpdatePayload) => void) => () => void;
    onDisconnectTimer: (handler: (payload: DisconnectTimerPayload) => void) => () => void;
    onPlayerReconnected: (handler: (payload: PlayerReconnectedPayload) => void) => () => void;
    onPlayerDisconnected: (handler: (payload: PlayerDisconnectedPayload) => void) => () => void;
    onError: (handler: (payload: SocketErrorPayload) => void) => () => void;
    onReconnected: (handler: (response: JoinRoomResponse) => void) => () => void;
}

/**
 * useGameSocket hook
 * 
 * Provides socket connection and event handling functions.
 * Connection must be initiated manually via connectAndJoinRoom() or connect().
 * 
 * @returns Socket connection and event handling functions
 */
export const useGameSocket = (): UseGameSocketReturn => {
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
        gameSocketService.getStatus()
    );

    // Subscribe to connection status changes
    useEffect(() => {
        const unsubscribe = gameSocketService.onStatusChange((status) => {
            setConnectionStatus(status);
        });

        return () => {
            unsubscribe();
            // Note: We don't disconnect here because the socket service is a singleton
            // and might be used by other components. Disconnect only on cleanup if needed.
        };
    }, []);

    // Note: We do NOT call removeAllListeners() here. Both GameStateProvider and GamePage
    // use this hook, so when either unmounts (e.g. Strict Mode unmounts child first),
    // removeAllListeners() would wipe the other's handlers (e.g. onPhaseChanged/onIslandsPlaced).
    // Each subscriber (e.g. GameStateContext) cleans up its own listeners via the
    // unsubscribe returned from onPhaseChanged etc., so the socket stays correct.

    // Memoized connection and room management functions
    const connectAndJoinRoom = useCallback(
        (
            payload: JoinRoomPayload,
            options?: { timeout?: number; maxRetries?: number }
        ): Promise<JoinRoomResponse> => {
            return gameSocketService.connectAndJoinRoom(payload, options);
        },
        []
    );

    const joinRoom = useCallback(
        (payload: JoinRoomPayload, callback?: (response: JoinRoomResponse) => void) => {
            gameSocketService.joinRoom(payload, callback);
        },
        []
    );

    const leaveRoom = useCallback(
        (payload: LeaveRoomPayload, callback?: (response: SocketResponse) => void) => {
            gameSocketService.leaveRoom(payload, callback);
        },
        []
    );

    const disconnect = useCallback(() => {
        gameSocketService.disconnect();
    }, []);

    const playerReady = useCallback(
        (payload: PlayerReadyPayload, callback?: (response: SocketResponse) => void) => {
            gameSocketService.playerReady(payload, callback);
        },
        []
    );

    const placeIslands = useCallback(
        (payload: PlaceIslandsPayload, callback?: (response: PlaceIslandsResponse) => void) => {
            gameSocketService.placeIslands(payload, callback);
        },
        []
    );

    const placeMonster = useCallback(
        (payload: PlaceMonsterPayload, callback?: (response: PlaceMonsterResponse) => void) => {
            gameSocketService.placeMonster(payload, callback);
        },
        []
    );

    const moveMonster = useCallback(
        (payload: MoveMonsterPayload, callback?: (response: MoveMonsterResponse) => void) => {
            gameSocketService.moveMonster(payload, callback);
        },
        []
    );

    const concede = useCallback((callback?: (response: ConcedeResponse) => void) => {
        gameSocketService.concede(callback);
    }, []);

    const updateMonsterDeduction = useCallback(
        (payload: UpdateMonsterDeductionPayload, callback?: (response: UpdateMonsterDeductionResponse) => void) => {
            gameSocketService.updateMonsterDeduction(payload, callback);
        },
        []
    );

    const getPlayerDeductions = useCallback((callback?: (response: GetPlayerDeductionsResponse) => void) => {
        gameSocketService.getPlayerDeductions(callback);
    }, []);

    const sendChatMessage = useCallback(
        (payload: ChatMessagePayload, callback?: (response: SocketResponse) => void) => {
            gameSocketService.sendChatMessage(payload, callback);
        },
        []
    );

    const getChatHistory = useCallback(
        (payload: GetChatHistoryPayload, callback?: (response: GetChatHistoryResponse) => void) => {
            gameSocketService.getChatHistory(payload, callback);
        },
        []
    );

    const getHistory = useCallback(
        (payload: GetHistoryPayload, callback?: (response: GetHistoryResponse) => void) => {
            gameSocketService.getHistory(payload, callback);
        },
        []
    );

    const changeTheme = useCallback(
        (payload: ChangeThemePayload, callback?: (response: SocketResponse) => void) => {
            gameSocketService.changeTheme(payload, callback);
        },
        []
    );

    const changeColor = useCallback(
        (payload: ChangeColorPayload, callback?: (response: SocketResponse) => void) => {
            gameSocketService.changeColor(payload, callback);
        },
        []
    );

    const startGame = useCallback(
        (callback?: (response: StartGameResponse) => void) => {
            gameSocketService.startGame(callback);
        },
        []
    );

    // Memoized subscribe functions
    const onRoomUpdate = useCallback(
        (handler: (payload: RoomUpdatePayload) => void) => {
            return gameSocketService.onRoomUpdate(handler);
        },
        []
    );

    const onPlayerJoined = useCallback(
        (handler: (payload: PlayerJoinedPayload) => void) => {
            return gameSocketService.onPlayerJoined(handler);
        },
        []
    );

    const onPlayerLeft = useCallback(
        (handler: (payload: PlayerLeftPayload) => void) => {
            return gameSocketService.onPlayerLeft(handler);
        },
        []
    );

    const onPlayerReadyUpdate = useCallback(
        (handler: (payload: PlayerReadyUpdatePayload) => void) => {
            return gameSocketService.onPlayerReadyUpdate(handler);
        },
        []
    );

    const onPhaseChanged = useCallback(
        (handler: (payload: PhaseChangedPayload) => void) => {
            return gameSocketService.onPhaseChanged(handler);
        },
        []
    );

    const onIslandsPlaced = useCallback(
        (handler: (payload: IslandsPlacedPayload) => void) => {
            return gameSocketService.onIslandsPlaced(handler);
        },
        []
    );

    const onMonsterPlaced = useCallback(
        (handler: (payload: MonsterPlacedPayload) => void) => {
            return gameSocketService.onMonsterPlaced(handler);
        },
        []
    );

    const onMonsterMoved = useCallback(
        (handler: (payload: MonsterMovedPayload) => void) => {
            return gameSocketService.onMonsterMoved(handler);
        },
        []
    );

    const onGameEnded = useCallback(
        (handler: (payload: GameEndedPayload) => void) => {
            return gameSocketService.onGameEnded(handler);
        },
        []
    );

    const onTurnChanged = useCallback(
        (handler: (payload: TurnChangedPayload) => void) => {
            return gameSocketService.onTurnChanged(handler);
        },
        []
    );

    const onChatMessage = useCallback(
        (handler: (payload: ChatMessageReceivedPayload) => void) => {
            return gameSocketService.onChatMessage(handler);
        },
        []
    );

    const onHistoryUpdate = useCallback(
        (handler: (payload: HistoryUpdatePayload) => void) => {
            return gameSocketService.onHistoryUpdate(handler);
        },
        []
    );

    const onDisconnectTimer = useCallback(
        (handler: (payload: DisconnectTimerPayload) => void) => {
            return gameSocketService.onDisconnectTimer(handler);
        },
        []
    );

    const onPlayerReconnected = useCallback(
        (handler: (payload: PlayerReconnectedPayload) => void) => {
            return gameSocketService.onPlayerReconnected(handler);
        },
        []
    );

    const onPlayerDisconnected = useCallback(
        (handler: (payload: PlayerDisconnectedPayload) => void) => {
            return gameSocketService.onPlayerDisconnected(handler);
        },
        []
    );

    const onError = useCallback(
        (handler: (payload: SocketErrorPayload) => void) => {
            return gameSocketService.onError(handler);
        },
        []
    );

    const onReconnected = useCallback((handler: (response: JoinRoomResponse) => void) => {
        return gameSocketService.onReconnected(handler);
    }, []);

    return {
        // Connection info
        socket: gameSocketService.getSocket(),
        connectionStatus,
        isConnected: connectionStatus === 'connected',

        // Connection and room management
        connectAndJoinRoom,
        joinRoom,
        leaveRoom,
        disconnect,
        playerReady,
        placeIslands,
        placeMonster,
        moveMonster,
        concede,
        updateMonsterDeduction,
        getPlayerDeductions,
        sendChatMessage,
        getChatHistory,
        getHistory,
        changeTheme,
        changeColor,
        startGame,

        // Subscribe functions
        onRoomUpdate,
        onPlayerJoined,
        onPlayerLeft,
        onPlayerReadyUpdate,
        onPhaseChanged,
        onIslandsPlaced,
        onMonsterPlaced,
        onMonsterMoved,
        onGameEnded,
        onTurnChanged,
        onChatMessage,
        onHistoryUpdate,
        onDisconnectTimer,
        onPlayerReconnected,
        onPlayerDisconnected,
        onError,
        onReconnected,
    };
};

export default useGameSocket;
